<template>
  <div class="boosting-main">
    <div class="boosting-toolbar" v-if="hasPages">
      <ReportingDatePicker
        :date-start="dateStart"
        :date-end="dateEnd"
        :compare-mode="compareMode"
        @apply="onDatePickerApply"
      />
      <button
        class="refresh-btn"
        :disabled="!selectedPages.length"
        :title="$t('dash.reporting.force_refresh')"
        @click="refreshKey++"
      >
        <i class="material-icons">refresh</i>
      </button>
    </div>

    <div class="boosting-content">
      <div class="boosting-empty" v-if="!hasPages">
        <i class="material-icons">trending_up</i>
        <p>{{ $t('dash.boosting.no_pages') }}</p>
      </div>

      <div class="boosting-empty" v-else-if="!selectedPages.length">
        <i class="material-icons">trending_up</i>
        <p>{{ $t('dash.boosting.select_page') }}</p>
        <p class="boosting-explain">{{ $t('dash.boosting.explain') }}</p>
      </div>

      <template v-else>
        <div class="comparison-tab-bar" v-if="isMultiPage">
          <div class="ctab ctab--global" :class="{ active: activeTab === 'global' }" @click="activeTab = 'global'">
            <i class="material-icons ctab-icon">dashboard</i>
            <span class="ctab-label">{{ $t('dash.boosting.global') }}</span>
          </div>
          <div class="ctab-sep"></div>
          <div
            class="ctab"
            v-for="page in selectedPages"
            :key="page.id"
            :class="{ active: activeTab === page.id }"
            @click="activeTab = page.id"
          >
            <div class="ctab-avatar-wrap">
              <img v-if="page.avatar" :src="page.avatar" class="ctab-avatar" alt="">
              <span v-else class="ctab-avatar-fallback">{{ (page.name || '?')[0].toUpperCase() }}</span>
              <img v-if="networkIcon(page.network)" :src="networkIcon(page.network)" class="ctab-net-badge" alt="">
            </div>
            <span class="ctab-label">{{ page.name }}</span>
          </div>
        </div>

        <BoostingPage
          :key="`${activeTab}-${activePages.map(p => p.id).join(',')}-${dateStart}-${dateEnd}-${compareMode}-${refreshKey}`"
          :pages="activePages"
          :global="isMultiPage && activeTab === 'global'"
          :date-start="dateStart"
          :date-end="dateEnd"
          :prev-range="prevRange"
        />
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import BoostingPage from './BoostingPage.vue'
import ReportingDatePicker from '../reporting-web/ReportingDatePicker.vue'
import { isBoostingPage } from '@/utils/boosting'

export default {
  name: 'Boosting',
  components: { BoostingPage, ReportingDatePicker },

  data() {
    return {
      dateStart: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      dateEnd: moment().format('YYYY-MM-DD'),
      compareMode: 'previous',
      refreshKey: 0,
      activeTab: 'global',
    }
  },

  computed: {
    allPages() {
      return this.$store.getters['selectPage/selectPageAllPages']
    },
    hasPages() {
      return Object.values(this.allPages).some(isBoostingPage)
    },
    selectedPages() {
      return this.$store.getters['selectPage/selectPagePageSelected']
        .map(id => this.allPages[id])
        .filter(isBoostingPage)
    },
    isMultiPage() {
      return this.selectedPages.length > 1
    },
    activePages() {
      if (!this.isMultiPage || this.activeTab === 'global') return this.selectedPages
      var page = this.selectedPages.find(p => p.id === this.activeTab)
      return page ? [page] : this.selectedPages
    },
    periodDays() {
      return moment(this.dateEnd).diff(moment(this.dateStart), 'days') + 1
    },
    effectiveCompareMode() {
      var days = this.periodDays
      if (this.compareMode === 'month' && days > 31) return 'previous'
      if (this.compareMode === 'year' && days > 365) return 'previous'
      return this.compareMode
    },
    prevRange() {
      if (this.compareMode === 'none') return null
      const mode = this.effectiveCompareMode
      let compStart, compEnd
      if (mode === 'month') {
        compStart = moment(this.dateStart).subtract(1, 'month')
        compEnd   = moment(this.dateEnd).subtract(1, 'month')
      } else if (mode === 'year') {
        compStart = moment(this.dateStart).subtract(1, 'year')
        compEnd   = moment(this.dateEnd).subtract(1, 'year')
      } else {
        compEnd   = moment(this.dateStart).subtract(1, 'day')
        compStart = compEnd.clone().subtract(this.periodDays - 1, 'days')
      }
      return { since: compStart.unix(), until: compEnd.clone().endOf('day').unix() }
    },
  },

  watch: {
    selectedPages(newPages, oldPages) {
      var ids = pages => pages.map(p => p.id).join(',')
      if (ids(newPages) !== ids(oldPages)) this.activeTab = 'global'
    },
  },

  methods: {
    networkIcon(network) {
      const map = {
        facebook:  require('@/assets/img/iconsNetwork/ico-facebook.png'),
        instagram: require('@/assets/img/iconsNetwork/ico-instagram.png'),
        linkedin:  require('@/assets/img/iconsNetwork/ico-linkedin.png'),
        tiktok:    require('@/assets/img/iconsNetwork/ico-tiktok.png'),
      }
      return map[network] || null
    },

    onDatePickerApply({ dateStart, dateEnd, compareMode }) {
      this.dateStart   = dateStart
      this.dateEnd     = dateEnd
      this.compareMode = compareMode
      this.refreshKey++
    },
  },
}
</script>

<style scoped>
.boosting-main {
  height: 100%;
  background: #f4f0f8;
  display: flex;
  flex-direction: column;
}
.boosting-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 5.6px !important;
  padding: 10px 16px;
  box-shadow: 0 2px 12px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
  flex-shrink: 0;
  margin: 20px 24px 0;
  position: relative;
  z-index: 100;
}
.boosting-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
}
.refresh-btn {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 8px;
  border: 1.5px solid #e0d0ea !important;
  background: #fff !important;
  color: #6B1F4D !important;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.refresh-btn:hover { background: #f1edf5; border-color: #6B1F4D; }
.refresh-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.refresh-btn .material-icons { font-size: 16px; }
.comparison-tab-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border-radius: 5.6px !important;
  padding: 8px 10px;
  box-shadow: 0 2px 12px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
  flex-shrink: 0;
  overflow-x: auto;
  scrollbar-width: none;
}
.comparison-tab-bar::-webkit-scrollbar { display: none; }
.ctab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 5.6px;
  cursor: pointer;
  font-size: 13px;
  color: #555e75;
  font-weight: 500;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;
}
.ctab:hover { background: #f6f1fa; color: #6B1F4D; }
.ctab.active { background: #6B1F4D; color: #fff; }
.ctab-icon { font-size: 17px; }
.ctab-sep {
  width: 1px;
  height: 28px;
  background: #e0d0ea;
  flex-shrink: 0;
  margin: 0 4px;
}
.ctab-avatar-wrap {
  position: relative;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}
.ctab-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.ctab-avatar-fallback {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f1edf5;
  color: #6B1F4D;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}
.ctab.active .ctab-avatar-fallback { background: rgba(255,255,255,0.25); color: #fff; }
.ctab-net-badge {
  position: absolute;
  bottom: -2px;
  right: -3px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 1.5px solid #fff;
  object-fit: cover;
}
.ctab.active .ctab-net-badge { border-color: #6B1F4D; }
.boosting-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ba3b7;
}
.boosting-empty .material-icons { font-size: 48px; color: #e0d0ea; }
.boosting-empty p { font-size: 15px; }
.boosting-empty .boosting-explain { max-width: 520px; font-size: 13px; line-height: 1.5; color: #9ba3b7; text-align: center; margin: 0; }
</style>
